import type { Experiments } from '@freelancer/abtest/interface';
import type { AppInstallPromptDismissals } from '@freelancer/app-install-prompt';
import type {
  AiConsultantGeneratedProjectDraft,
  AiConsultantMessage,
  AiPlaygroundConversationSaveFile,
  AiPlaygroundSavedTool,
} from '@freelancer/datastore/collections';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { PostJobPageFormStates } from '@freelancer/datastore/collections/post-job-page-form-state';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { ProjectViewProject } from '@freelancer/datastore/collections/project-view-projects';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { Task } from '@freelancer/datastore/collections/tasks';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type {
  ThreadContext,
  ThreadType,
} from '@freelancer/datastore/collections/threads';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type {
  ResumeParsingStatus,
  UserParsedResume,
} from '@freelancer/datastore/collections/user-parsed-resumes';
import type { CurrentTimeTrackingSession } from '@freelancer/project-time-tracking';
// FIXME: T235847
// eslint-disable-next-line local-rules/validate-freelancer-imports
import type { SearchFilters } from '@freelancer/search-filters/search-filters.model';
import type { Theme, ThemeSetting } from '@freelancer/theme';
import type { VideochatCalling } from '@freelancer/videochat';
import type { ViewedItemsMap } from '@freelancer/viewed-items';
import type { RoleApi } from 'api-typings/common/common';
import type { DevelopersPreference } from 'developers/developers-preference.model';

/**
 *  Holds the types which can be stored in local storage i.e. are JSON serialisable.
 *  Values can be restricted to solely their type,
 *  or can also be allowed to be `null` (if the item needs to be able to be deleted).
 */
export interface LocalStorageTypes {
  /* FLN LOCAL STORAGE TYPES */
  readonly complianceQueueApproveDenyFormStates: {
    readonly [supportSessionId: number]: {
      readonly [formField: string]: string | boolean | null;
    };
  };
  readonly deloitteDemo: boolean; // Used to show hidden Deloitte features for demonstration purposes.
  readonly developersPreference: DevelopersPreference | null;
  readonly postProjectObject: ProjectViewProject | null; // We can stop using this once login/signup is in webapp
  readonly projectOverlayThreadComplete: {
    readonly [threadId: number]: boolean;
  };
  readonly projectOverlayFullFlowComplete: boolean;
  readonly webappChats: readonly SavedChatAttributes[];
  readonly webappChatDraftMessages: {
    readonly [threadIdentifier: string]: DraftMessage | null;
  };
  readonly webappThreadListMinimise: boolean;
  readonly taskList: TaskListState | null;
  readonly taskListCurrentTaskClean: boolean | null;
  readonly autoCloseChatEnabled: boolean;

  readonly lastSeenPjpAssistant: LastSeenPjpAssistant;
  readonly hireMeDraft: HireMeDraft | null;

  readonly navSavedAlertsLastReadTime: number | null;
  readonly navUpdatesLastReadTime: number | null;
  readonly navNotificationUpdatesLastReadTime: number | null;

  readonly dashboardMyProjectsUserType: RoleApi | null;

  readonly enterpriseContactFormSubmitted: boolean; // Used on the enterprise landing page

  readonly postJobPageDraft: PostJobPageFormStates | null;

  readonly manageRecentTable: ManageRecentTable | null;

  readonly inviteToBidDiscardedFreelancers: {
    readonly [projectId: number]: readonly number[];
  };

  readonly flCardsExpandedStatus: {
    readonly [flCardId: string]: boolean;
  };

  readonly abTestVariations: ABTestVariationCache;

  readonly viewedItems: ViewedItemsMap;

  // Hide the mobile new message toast
  readonly hideMobileNewMessageToast: boolean;

  readonly searchServiceFilters: SearchFilters;

  // For saving the user's time tracking session across tabs
  readonly currentTimeTrackingSession: CurrentTimeTrackingSession | null;

  /**
   * Used on the CV upload step of the freelancer onboarding flow
   * TODO: T236205 Remove after endpoint allows fetching the uploaded file
   */
  readonly freelancerOnboardingResumeUploaded: boolean;

  // Parsed resume object from CV upload step of the freelancer onboarding flow
  readonly freelancerOnboardingParsedResume: UserParsedResume | null;

  // Used to check if CV is parsed successfully
  readonly freelancerOnboardingResumeParsingStatus: ResumeParsingStatus | null;

  // Use that whenever you need a unique ID for a particular device
  readonly deviceId: string | null;

  readonly dontAskAgainLastChecked: number;

  readonly nativeRatingPrompt: {
    // When was the last time the native rating prompt was shown?
    readonly lastPrompted: {
      readonly date: number;
      readonly leftFeedbackOrRating: boolean;
    };
    // If the user has been prompted to rate the app on project acceptance or
    // milestone release. This should only be asked for once.
    readonly oneOff: {
      readonly acceptProjectOrMilestoneRelease?: boolean;
    };
  };

  // For saving unfinished forms to be resumed later.
  readonly lastUsedForms: LastUsedForms;

  /* TYPES USED FOR TESTING ONLY */
  readonly testProperty: number;
  readonly testName: string | null;
  readonly testProfile: MockProfile;
  readonly externalQuotation: ExternalQuotation | null;

  // For saving cookie to be used after 3DS completed
  readonly threeDSChallengeCookie: string;

  // For saving whether notifications have been requested at login before.
  readonly notificationsRequested: boolean;

  // For saving loads which are pending actions which is used for blocking contact details when viewing subsequent loads
  readonly loadIdPendingAction: number | null;

  readonly groupsHomepageSelectedView: GroupsViewFilterSelection;

  // For indicating the waiting screen steps
  readonly waitingScreenSteps: WaitingScreenSteps;

  readonly giveGetReferrerUsername: string | null;
  readonly giveGetFooterClosed: boolean;

  // Store agent preferred ai prompt selection
  readonly aiPromptSelection: number | null;
  readonly aiPromptEnabled: boolean;

  // For saving the chat history with AI Consultant
  readonly aiConsultantChatHistory: readonly AiConsultantMessage[] | null;
  // For saving the project data generated by AI Consultant
  readonly aiConsultantGeneratedProjectDraft: AiConsultantGeneratedProjectDraft | null;

  // For saving the conversation data in the AI Playground
  readonly aiPlaygroundConversationStorage:
    | readonly AiPlaygroundConversationSaveFile[]
    | null;

  // For saving currently saved tools in the AI playground
  readonly aiPlaygroundSavedTools: readonly AiPlaygroundSavedTool[] | null;

  // For saving the user's preferred theme
  readonly theme: ThemeSetting | null;

  // For saving the user's original theme (for use in pages with forced dark mode)
  readonly userOriginalTheme: Theme | null;

  // For recording the last time the user dismissed prompts to install the app that we want to remember between sessions
  readonly appInstallPromptDismissals: AppInstallPromptDismissals;
  // The number of days after which a user who dismissed an email install prompt should see it again
  readonly appInstallPromptEmailDismissalDays: number;

  // Used in contact syncing
  readonly doNotAskContactsSyncAgain: boolean;

  // Call state for incoming calls
  readonly videochatCalling: VideochatCalling | null;
}

/*
 * The interfaces below are interfaces needed by `chat-box` and `helpers`
 */

export interface DraftMessage {
  readonly text: string;
  readonly lastUpdated: number;
}

export enum FocusState {
  Header,
  HeaderAndTypingBox,
  Neither,
}

export interface SavedChatAttributes {
  readonly minimised: boolean;
  readonly focus: FocusState;
  readonly width: number;
  readonly height: number;
  readonly threadIdentifier: ThreadIdentifier;
  // This will be used in the new chatbox. `width` and `height`
  // will be deprecated once we complete the chatbox migration
  readonly dimensions?: ChatBoxDimensions;
  readonly origin?: string;
  readonly timeCreated?: number;
}

export interface WaitingScreenSteps {
  readonly projectId: number;
  readonly formStep: number;
  readonly vehicleStep: number;
}

export interface ChatBoxDimensions {
  readonly width: number;
  readonly height: number;
}

export interface ThreadIdentifier {
  readonly id: number; // TODO: T267853 - consider making id possibly undefined and getting rid of isFake
  readonly threadType: ThreadType;
  readonly context: ThreadContext;
  readonly members: readonly number[];
  readonly isFake: boolean; // TODO: T267853 - needed?
}

/* Interface used for TESTING ONLY */
interface MockProfile {
  readonly id: number;
  readonly name: string;
}

export interface HireMeDraft {
  readonly budget: number;
  readonly currencyId: number;
  readonly freelancerId: number;
  readonly hireFor: string;
  readonly hoursPerWeek: number;
  readonly message: string;

  // Optional field for backward compatibility.
  readonly confirmed?: boolean;
}

export interface LastSeenPjpAssistant {
  readonly time: number;
}

export interface TaskListState {
  readonly currentTaskClean?: boolean;
  readonly draftTask?: Partial<Task> | Task;
}

/**
 * Interface used for remembering recently viewed tables in /manage
 */
export interface ManageRecentTable {
  readonly employerTable: string;
  readonly collaboratorTable: string;
  readonly freelancerTable: string;
  readonly insourceTable: string;

  // This is the recently viewed table in general
  readonly manageTable?: string;
}

// To store the external sending data (i.e. email, phone, etc)
export interface ExternalQuotation {
  readonly email: string;
}

/**
 * Interfaces used for Generic Form Save functionality.
 */

export interface LastUsedForms {
  // The form section ID will be used as the key here (set by developer).
  // Inside the sectionId value it will contain the FormGroup object and its
  // whole contents.
  readonly [sectionId: string]: LastUsedFormsSection;
}

export interface LastUsedFormsSection {
  readonly [fieldId: string]: any;
}

/**
 * Interface used for AB Test caching
 */
export interface ABTestVariation<T extends keyof Experiments> {
  readonly variation: Experiments[T];
  readonly expiry: number;
}

export interface ABTestVariationCache {
  readonly [cacheKey: string]: ABTestVariation<keyof Experiments>;
}

/**
 * Interface for group homepage view state
 */
export type GroupsViewFilterSelection = 'grid' | 'list';
